/**
 * Consignaciones x Mayor — segmento único de todo lo consignado del módulo.
 *
 * Aquí se maneja lo que se entrega a un cliente para que él lo venda, en sus
 * dos formas:
 *
 *   - CONDUCES (el modelo nuevo): la entrega no es factura ni deuda. Se marca
 *     lo que el cliente reporta como vendido y de ahí sale una factura x mayor
 *     por cada tanda. Es la pestaña principal.
 *   - FACTURAS CONSIGNADAS (el modelo anterior, `consignado='S'`): la factura
 *     se emite completa desde el primer día y lo que el cliente no vende se
 *     DEVUELVE. Se sigue viendo y manteniendo aquí porque hay tenants con
 *     facturas así vivas y cobrándose; no se toca su comportamiento.
 *
 * Las facturas nacidas de un conduce solo se ven dentro de su conduce (no en
 * Facturas x Mayor), así que este segmento es su único acceso.
 *
 * Si el usuario dejó una entrega a medio llenar (borrador "conduce", ver
 * lib/borradorventa), un aviso arriba permite retomarla o descartarla.
 *
 * Todo el segmento está detrás de varinicial.usaconsignado='S', la misma llave
 * que gobierna la venta consignada y que el backend exige en cada endpoint.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, ActionCard } from "components/financepro";
import { tienePermisosModulo, puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";
import { useApi } from "lib/api";
import useAccionInicial from "lib/useAccionInicial";
import ConducesXmayor from "modules/ventasxmayor/conducesxmayor";
import ConsignadoPorCliente from "modules/ventasxmayor/consignadoporcliente";
import CrearConduceXmayor from "modules/ventasxmayor/crearconducexmayor";
import MantenimientoFacturasXmayor from "modules/ventasxmayor/mantenimientofacturasxmayor";
import { leerBorrador, borrarBorrador } from "lib/borradorventa";
import AvisoBorradorVenta from "components/borradorventa/AvisoBorradorVenta";

const tabSx = {
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.85rem",
  minHeight: 40,
  px: 2,
  borderRadius: 999,
  border: `1px solid ${FP.color.border}`,
  color: FP.color.textSecondary,
  bgcolor: "#FFFFFF",
  "& .MuiSvgIcon-root": { color: "inherit" },
  "&:hover": { borderColor: "#0B1220", color: "#0B1220" },
  "&.MuiTab-root.Mui-selected": {
    bgcolor: "#0B1220",
    borderColor: "#0B1220",
    color: "#FFFFFF !important",
  },
};

const avisoBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

export default function XmayorConsignaciones() {
  const [controller] = useMaterialUIController();
  const { roleDetalles, usuarioLog } = controller;
  const { post } = useApi();

  const puedeVer = tienePermisosModulo(roleDetalles, "conducesxmayor");
  const puedeEntregar = puedeAccionPrograma(roleDetalles, "conducesxmayor", "modificar");
  const puedeFacturasConsignadas = tienePermisosModulo(roleDetalles, "ares9720");

  const [config, setConfig] = useState(null);
  const [cargando, setCargando] = useState(true);
  // Valores con nombre: la pestaña de facturas consignadas depende del rol y
  // con índices las demás cambiarían de posición.
  const [tab, setTab] = useState("conduces");
  const [conduceNuevoOpen, setConduceNuevoOpen] = useState(false);
  // "Continuar" desde el aviso: el formulario abre con el borrador ya restaurado.
  const [restaurarConduce, setRestaurarConduce] = useState(false);
  const abrirConduce = (restaurar = false) => {
    setRestaurarConduce(restaurar);
    setConduceNuevoOpen(true);
  };

  // Entrega sin terminar de este usuario en este navegador; se relee al
  // cerrar el formulario.
  const [borradorConduce, setBorradorConduce] = useState(null);
  useEffect(() => {
    if (conduceNuevoOpen) return;
    setBorradorConduce(puedeEntregar ? leerBorrador("conduce", usuarioLog) : null);
  }, [conduceNuevoOpen, usuarioLog, puedeEntregar]);
  const descartarBorrador = () => {
    borrarBorrador("conduce", usuarioLog);
    setBorradorConduce(null);
  };
  const [refreshKey, setRefreshKey] = useState(0);
  const refrescar = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    post("/api/xmayor_config", {})
      .then(({ ok, data }) => {
        if (ok) setConfig(data);
      })
      .finally(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usaConsignado = config?.usaconsignado === "S";

  // Enlace profundo desde el buscador global (?accion=conduce).
  useAccionInicial({
    conduce: {
      permitido: puedeEntregar && usaConsignado,
      abrir: () => abrirConduce(),
    },
  });

  if (!puedeVer) {
    return (
      <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
        <PageHeader title="Consignaciones x Mayor" subtitle="Sin acceso" />
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={avisoBox}>
            <Typography sx={{ fontWeight: 700 }}>
              Su rol no tiene acceso a las consignaciones x mayor.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Consignaciones x Mayor"
        subtitle="Entregas al cliente, marcado de lo vendido y facturación de solo esos motores"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {cargando ? (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress size={30} />
          </Stack>
        ) : !usaConsignado ? (
          <Box sx={avisoBox}>
            <Typography sx={{ fontWeight: 800, mb: 0.6 }}>
              Las consignaciones están desactivadas
            </Typography>
            <Typography sx={{ fontSize: "0.86rem", color: FP.color.textSecondary }}>
              Para trabajar consignación hay que activarla en la configuración del sistema (usa
              consignado). Mientras esté apagada no se pueden registrar conduces ni facturar motores
              consignados.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Entrega en pausa (borrador guardado en este navegador) */}
            <AvisoBorradorVenta
              borrador={borradorConduce}
              etiqueta="entrega en consignación"
              onContinuar={() => abrirConduce(true)}
              onDescartar={descartarBorrador}
              sx={{ mb: 2 }}
            />
            {puedeEntregar && (
              <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <ActionCard
                    icon={LocalShippingOutlinedIcon}
                    titulo="Nueva entrega en consignación"
                    descripcion="Conduce de motores al cliente: sin factura, sin NCF y sin deuda"
                    color={FP.color.success}
                    onClick={() => abrirConduce()}
                  />
                </Grid>
              </Grid>
            )}

            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              scrollButtons={false}
              TabIndicatorProps={{ sx: { display: "none" } }}
              sx={{
                mb: 2.4,
                minHeight: 44,
                "& .MuiTabs-flexContainer": { gap: 1 },
                "& .MuiTab-root": { maxWidth: "none" },
              }}
            >
              <Tab
                value="conduces"
                icon={<LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Conduces"
                sx={tabSx}
              />
              <Tab
                value="cliente"
                icon={<PersonSearchOutlinedIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Por cliente"
                sx={tabSx}
              />
              {puedeFacturasConsignadas && (
                <Tab
                  value="facturas"
                  icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />}
                  iconPosition="start"
                  label="Facturas consignadas"
                  sx={tabSx}
                />
              )}
            </Tabs>

            {tab === "conduces" && <ConducesXmayor embedded refreshToken={refreshKey} />}

            {/* Todo lo consignado a un cliente, de todos sus conduces: se
                marca vendido y se factura en un solo paso. */}
            {tab === "cliente" && <ConsignadoPorCliente refreshToken={refreshKey} />}

            {tab === "facturas" && puedeFacturasConsignadas && (
              <>
                <Box sx={{ ...avisoBox, mb: 2.4 }}>
                  <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary }}>
                    Facturas consignadas del modelo anterior: se emitieron completas y el cliente
                    debe el total desde la entrega, así que lo que no vendió se <b>devuelve</b>{" "}
                    desde el detalle de la factura. Para entregar sin generar deuda, use un conduce.
                  </Typography>
                </Box>
                <MantenimientoFacturasXmayor embedded soloConsignadas refreshToken={refreshKey} />
              </>
            )}
          </>
        )}
      </Box>

      {/* Nueva entrega en consignación */}
      <Dialog fullScreen open={conduceNuevoOpen} onClose={() => setConduceNuevoOpen(false)}>
        <CrearConduceXmayor
          embedded
          restaurarBorrador={restaurarConduce}
          onExit={() => {
            setConduceNuevoOpen(false);
            refrescar();
          }}
          onSaved={refrescar}
        />
      </Dialog>
    </Box>
  );
}
